import { buildSrpFacetHref } from '../../pages/job/@jobId/@seoSlug/utils/buildSrpFacetHref';
import { findRoleForAnyLocaleTitle } from '../role/find-role-for-title';
import { buildRoleHubPath } from '../role/role-hub-config';
import { buildLocationSrpPath } from '../location/build-location-srp-path';
import type { LocationLocale } from '../location/generate-localized-location-url';

export interface TopRoleLink {
  role: string;
  href: string;
}

/** Top N role links for a company profile page — mirrors the roleDistribution StarburstChart's
 * data but as clickable job-search links, since the chart itself isn't clickable. */
export function buildTopRoleLinks(topRoles: string[] | undefined, locale: string, limit = 5): TopRoleLink[] {
  const loc = locale === 'fi' ? 'fi' : locale === 'sv' ? 'sv' : 'en';
  return (topRoles ?? []).slice(0, limit).map((role) => {
    // Configured roles link to the indexable role hub (localized display name as anchor text);
    // anything else falls back to the noindex normalisedTitle-filtered search.
    const hub = findRoleForAnyLocaleTitle(role);
    return hub
      ? { role: hub.displayNames[loc], href: buildRoleHubPath(hub.slug, loc) }
      : { role, href: buildSrpFacetHref(locale, 'normalisedTitle', role) };
  });
}

export interface TopLocationLink {
  location: string;
  href: string;
}

/** The company's single most common hiring location, as a link to that location's job hub —
 * mirrors the locationDistribution StarburstChart's top entry but as a clickable link. */
export function resolveTopLocationLink(
  locationDistribution: Record<string, number> | undefined,
  locale: LocationLocale,
): TopLocationLink | undefined {
  if (!locationDistribution) return undefined;
  const entries = Object.entries(locationDistribution);
  if (entries.length === 0) return undefined;
  const [location] = entries.sort((a, b) => b[1] - a[1])[0];
  return { location, href: `/${locale}${buildLocationSrpPath(location, locale)}` };
}
